// ClassItem.jsx

import React, { useState, useContext } from 'react';
import { useParams } from 'react-router-dom';
import dateFormat from 'dateformat';
import {
    Card, CardHeader, CardContent, CardActions, Typography, Button, Avatar, AvatarGroup,
    IconButton, Menu, MenuItem, Modal, Box, Snackbar, Alert, FormControl, InputLabel, Select
} from '@mui/material';
import {
    MoreVert, NotificationsActive, NotificationAdd, ContentCopy, Duo,
    CalendarToday, Schedule, Info
} from '@mui/icons-material';

import { AuthContext } from '../../../context/AuthContext';
import AddClassRemainder from '../../../actions/course/AddClassRemainder';
import JoinClass from '../../../actions/course/JoinClass';
import ModalStyle from '../../../data/ModalStyle';

const ClassItem = ({ item }) => {
    const { course_url, batch_id } = useParams();
    const { user } = useContext(AuthContext);

    // State for UI elements
    const [reminder, setReminder] = useState(item.reminder || false);
    const [modalOpen, setModalOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [reminderTime, setReminderTime] = useState('15'); // Default to 15 mins

    // Menu handling
    const handleMenuClick = (event) => setAnchorEl(event.currentTarget);
    const handleMenuClose = () => setAnchorEl(null);

    // Copy to clipboard
    const handleCopyLink = () => {
        navigator.clipboard.writeText(item.meeting_link);
        setSnackbar({ open: true, message: 'Meeting link copied to clipboard!', severity: 'success' });
        handleMenuClose();
    };

    // Reminder logic
    const handleSetReminder = async () => {
        const res = await AddClassRemainder({
            payload: {
                session: user.SessionId,
                course_url,
                batch_id,
                class_id: item.id,
                date_time: reminderTime,
                class_time: item.class_starts
            }
        });

        if (res) {
            setSnackbar({ open: true, message: res.msg, severity: res.status });
            if (res.status === "success") {
                setReminder(true);
                setModalOpen(false);
            }
        }
    };

    // Join class action
    const handleJoinClass = () => {
        JoinClass({
            payload: { course_url, batch_id, class_id: item.id, user: user.SessionId, status: "joined" }
        });
        window.open(item.meeting_link, '_blank');
    };

    return (
        <>
            <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>

            <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
                <Box sx={ModalStyle}>
                    <Typography variant="h6" component="h2">Set a Reminder</Typography>
                    <Typography sx={{ mt: 2 }}>Choose when you'd like to be notified before the class starts.</Typography>
                    <FormControl fullWidth sx={{ mt: 3 }}>
                        <InputLabel>Remind Me</InputLabel>
                        <Select
                            value={reminderTime}
                            label="Remind Me"
                            onChange={(e) => setReminderTime(e.target.value)}
                        >
                            <MenuItem value={5}>5 minutes before</MenuItem>
                            <MenuItem value={15}>15 minutes before</MenuItem>
                            <MenuItem value={30}>30 minutes before</MenuItem>
                            <MenuItem value={24 * 60}>1 day before</MenuItem> {/* Value in minutes */}
                        </Select>
                    </FormControl>
                    <Button variant="contained" fullWidth sx={{ mt: 3 }} onClick={handleSetReminder}>
                        Schedule Reminder
                    </Button>
                </Box>
            </Modal>

            <Card sx={{ borderRadius: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                <CardHeader
                    titleTypographyProps={{ fontWeight: 'bold' }}
                    title={item.class_title}
                    action={
                        <IconButton onClick={handleMenuClick}>
                            <MoreVert />
                        </IconButton>
                    }
                />
                <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                    <MenuItem onClick={handleCopyLink}>
                        <ContentCopy sx={{ mr: 1 }} /> Copy Meeting Link
                    </MenuItem>
                </Menu>

                <CardContent sx={{ pt: 0 }}>
                    <Box display="flex" justifyContent="space-between" color="text.secondary" sx={{ mb: 2 }}>
                        <Box display="flex" alignItems="center"><CalendarToday sx={{ fontSize: 16, mr: 1 }} /> {dateFormat(item.class_starts, "dS mmm, yyyy")}</Box>
                        <Box display="flex" alignItems="center"><Schedule sx={{ fontSize: 16, mr: 1 }} /> {dateFormat(item.class_starts, "h:MM TT")}</Box>
                    </Box>
                    <Box display="flex" alignItems="flex-start" color="text.secondary">
                        <Info sx={{ fontSize: 16, mr: 1, mt: 0.5 }} />
                        <Typography variant="body2" sx={{ maxHeight: 40, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            <b>Topics:</b> {item.class_description || "Not available"}
                        </Typography>
                    </Box>
                </CardContent>
                <hr style={{ border: 'none', borderTop: '1px solid #eee' }} />
                <CardActions sx={{ p: 2, justifyContent: 'space-between' }}>
                    <AvatarGroup max={4}>
                        {Array.isArray(item.attendees)&&item.attendees.map(attendee => (
                            <Avatar key={attendee.user_id} alt={attendee.first_name} src={attendee.profile_pic} sx={{ width: 30, height: 30 }} />
                        ))}
                    </AvatarGroup>
                    <Box>
                        {reminder ? (
                            <Button variant="outlined" color="success" startIcon={<NotificationsActive />} disabled>
                                Reminder Set
                            </Button>
                        ) : (
                            <Button variant="outlined" startIcon={<NotificationAdd />} onClick={() => setModalOpen(true)}>
                                Set Reminder
                            </Button>
                        )}
                        <Button variant="contained" sx={{ ml: 1 }} startIcon={<Duo />} onClick={handleJoinClass}>
                            Join Class
                        </Button>
                    </Box>
                </CardActions>
            </Card>
        </>
    );
};

export default ClassItem;